import React, { useState } from "react";
import { Popover } from "@mui/material";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { isFilterActive, pickedValues } from "./columnFilters";
import "./FilterHeaderCell.css";

// A list this long gets a box to search it.
const SEARCHABLE_OVER = 8;

// One checkbox row of a select filter's list.
const FilterOption = ({ checked, onClick, children }) => (
  <li>
    <button
      type="button"
      role="option"
      aria-selected={checked}
      className={checked ? "col-filter-option col-filter-option-on" : "col-filter-option"}
      onClick={onClick}
    >
      {checked ? <CheckBoxRoundedIcon /> : <CheckBoxOutlineBlankRoundedIcon />}
      <span>{children}</span>
    </button>
  </li>
);

// A table header cell: the column name with a filter button beside it. The
// button opens a small panel - a text box for a "text" column, the column's
// values (see columnFilters.filterOptions) as checkboxes for a "select" one,
// any number of which can be picked. The button is filled in while the column
// is filtered; ActiveFilters lists what is applied.
// `Cell` is the header cell element - a plain <th>, or MUI's TableCell (with
// `cellProps`) in a MUI table.
function FilterHeaderCell({ label, kind, value, options = [], onChange, Cell = "th", cellProps = {} }) {
  const [anchor, setAnchor] = useState(null);
  const [query, setQuery] = useState("");
  const active = isFilterActive(value);
  const picked = kind === "select" ? pickedValues(value) : [];
  const close = () => {
    setAnchor(null);
    setQuery("");
  };

  const term = query.trim().toLowerCase();
  const listed = term
    ? options.filter((option) => option.label.toLowerCase().includes(term))
    : options;

  // Picks keep the options' order, so the chip above the table reads the same
  // way the list does.
  const pickOnly = (values) => {
    const wanted = new Set(values);
    onChange(options.map((option) => option.value).filter((v) => wanted.has(v)));
  };
  const toggle = (optionValue) =>
    pickOnly(
      picked.includes(optionValue)
        ? picked.filter((v) => v !== optionValue)
        : [...picked, optionValue]
    );
  // With a search typed, "Select all" works on the matches only.
  const allListedPicked = listed.length > 0 && listed.every((option) => picked.includes(option.value));
  const toggleListed = () => {
    const listedValues = listed.map((option) => option.value);
    pickOnly(
      allListedPicked
        ? picked.filter((v) => !listedValues.includes(v))
        : [...picked, ...listedValues]
    );
  };

  return (
    <Cell {...cellProps}>
      <div className="col-head">
        <span>{label}</span>
        <button
          type="button"
          className={active ? "col-filter-btn col-filter-btn-on" : "col-filter-btn"}
          onClick={(event) => setAnchor(event.currentTarget)}
          aria-label={active ? `Filter ${label} (on)` : `Filter ${label}`}
          aria-haspopup="dialog"
          title={
            !active
              ? "Filter this column"
              : kind === "select"
              ? `${picked.length} selected - click to change`
              : "Filtered - click to change"
          }
        >
          <FilterListRoundedIcon />
          {kind === "select" && picked.length > 1 && (
            <span className="col-filter-count">{picked.length}</span>
          )}
        </button>
      </div>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: "col-filter-panel", elevation: 0 }}
      >
        <div className="col-filter-title">
          Filter {label}
          {kind === "select" && picked.length > 0 && (
            <span className="col-filter-picked">{picked.length} selected</span>
          )}
        </div>

        {kind === "select" ? (
          <>
            {options.length > SEARCHABLE_OVER && (
              <div className="col-filter-search">
                <SearchRoundedIcon />
                <input
                  autoFocus
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search..."
                  aria-label={`Search ${label} values`}
                />
              </div>
            )}
            <ul
              className="col-filter-list"
              role="listbox"
              aria-multiselectable="true"
              aria-label={label}
            >
              {listed.length > 1 && (
                <FilterOption checked={allListedPicked} onClick={toggleListed}>
                  {term ? `Select all ${listed.length} matches` : "Select all"}
                </FilterOption>
              )}
              {listed.map((option) => (
                <FilterOption
                  key={option.value}
                  checked={picked.includes(option.value)}
                  onClick={() => toggle(option.value)}
                >
                  {option.label}
                </FilterOption>
              ))}
              {listed.length === 0 && <li className="col-filter-none">No matches</li>}
            </ul>
            <div className="col-filter-actions">
              <button
                type="button"
                className="col-filter-action"
                onClick={() => onChange([])}
                disabled={picked.length === 0}
              >
                Clear
              </button>
              <button type="button" className="col-filter-action col-filter-action-done" onClick={close}>
                Done
              </button>
            </div>
          </>
        ) : (
          <form
            className="col-filter-text"
            onSubmit={(event) => {
              event.preventDefault();
              close();
            }}
          >
            <div className="col-filter-search">
              <SearchRoundedIcon />
              <input
                autoFocus
                value={value ?? ""}
                onChange={(event) => onChange(event.target.value)}
                placeholder={`Contains...`}
                aria-label={`Filter ${label}`}
              />
              {active && (
                <button type="button" className="col-filter-clear" onClick={() => onChange("")} aria-label="Clear">
                  <CloseRoundedIcon />
                </button>
              )}
            </div>
            <div className="col-filter-hint">Filters as you type. Enter to close.</div>
          </form>
        )}
      </Popover>
    </Cell>
  );
}

// The applied header filters as removable chips above the table, with a way
// to clear them all. Renders nothing when no column is filtered.
export function ActiveFilters({ specs, labels, filters, options = {}, onChange, onClear }) {
  const applied = specs.filter(({ field }) => isFilterActive(filters[field]));
  if (applied.length === 0) return null;

  // Up to this many picked values are named on a chip; the rest are counted.
  const NAMED = 2;
  const shownValue = ({ field, kind }) => {
    const value = filters[field];
    if (kind === "select") {
      const names = pickedValues(value).map(
        (picked) => (options[field] ?? []).find((option) => option.value === picked)?.label ?? picked
      );
      const more = names.length - NAMED;
      return {
        text: more > 0 ? `${names.slice(0, NAMED).join(", ")} +${more} more` : names.join(", "),
        title: names.join(", "),
      };
    }
    return { text: `"${String(value).trim()}"` };
  };

  return (
    <div className="col-chips" aria-label="Applied filters">
      <span className="col-chips-label">Filtered by</span>
      {applied.map((spec) => {
        const { text, title } = shownValue(spec);
        return (
          <span key={spec.field} className="col-chip" title={title}>
            <span className="col-chip-field">{labels[spec.field]}:</span> {text}
            <button
              type="button"
              onClick={() => onChange(spec.field, spec.kind === "select" ? [] : "")}
              aria-label={`Remove ${labels[spec.field]} filter`}
            >
              <CloseRoundedIcon />
            </button>
          </span>
        );
      })}
      <button type="button" className="col-chips-clear" onClick={onClear}>
        Clear all
      </button>
    </div>
  );
}

export default FilterHeaderCell;
